<template>
  <div class="rounded-md sm:rounded-lg sm:overflow-hidden">
    <div class="divide-y divide-gray-200">
      <div class="flex flex-col py-4">
        <div v-if="loading">
          <Skeleton v-for="i in 3" :key="i" class="h-40 w-full rounded-xl my-4" />
        </div>
        <div
          v-else-if="loyaltyCards?.length"
          class="flex flex-wrap gap-4"
        >
          <div
            v-for="card in loyaltyCards"
            :key="card.id"
            class="flex-1 w-full md:max-w-lg"
          >
            <div class="bg-white flex flex-col sm:flex-row rounded-xl min-h-40 shadow border-gray-200">
              <div class="w-60 flex-shrink-0 flex items-center pl-2 mx-auto">
                <img
                  v-if="card.file"
                  :src="card.file.renderPath"
                  class="rounded-xl h-40 w-full border-gray-200"
                >
                <div
                  v-else
                  class="rounded-xl h-40 w-full bg-gray-200 border-gray-200"
                />
              </div>

              <div class="flex flex-col flex-2 ml-3 py-3 pr-3">
                <h3 class="text-lg font-medium text-gray-900">
                  {{ card.organization?.morphable?.name }}
                </h3>

                <p class="mt-1 text-sm text-gray-500">
                  {{ card.name }}
                </p>

                <p
                  v-if="card.membership"
                  class="text-sm text-gray-500"
                >
                  Activé le {{ card.membership.formatted.created_at }}
                </p>

                <div
                  v-if="card.membership"
                  class="flex flex-1 flex-col justify-between"
                >
                  <loyalty-card-membership-point-balance
                    v-if="LoyaltyCardService().hasPointRewards(card.membership.loyalty_card)"
                    class="text-lg font-semibold py-2"
                    :formatted-point-balance="card.membership.formatted.point_balance"
                    :point-balance="card.membership.point_balance"
                    :rate="card.membership.loyalty_card.point?.rate || 0"
                  />
                  <LoyaltyCardMembershipProductBalance
                    v-else-if="LoyaltyCardService().hasProductRewards(card.membership.loyalty_card)"
                    class="text-lg font-semibold py-2"
                    :product-balance="card.membership.unit_balance"
                    :formatted-product-balance="card.membership.formatted.unit_balance"
                  />
                  <div class="flex flex-1 items-end justify-end">
                    <BaseButton primary density="comfortable" @click="showMembership(card.membership)">
                      Gérer la carte
                    </BaseButton>
                  </div>
                </div>

                <div
                  v-else
                  class="mt-4 flex flex-1 flex-col justify-between"
                >
                  <div
                    v-for="objective in card.objectives"
                    :key="objective.id"
                  >
                    <p>
                      {{ objective.reward.teaser }}
                    </p>
                  </div>
                  <div class="flex flex-1 items-end justify-end">
                    <BaseButton
                      primary
                      density="comfortable"
                      :loading="cardLoading?.loyaltyCardId && cardLoading.loyaltyCardId === card.id ? cardLoading.loading : false"
                      @click="activate(card)"
                    >
                      Activer la carte
                    </BaseButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
        >
          <div class="rounded-lg bg-white shadow-lg border overflow-hidden">
            <div class="flex flex-col lg:flex-row items-center gap-8 p-8 lg:p-12">
              <div class="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left space-y-6">
                <div class="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
                  <LucideCreditCard class="w-8 h-8 text-white" />
                </div>

                <div class="space-y-3">
                  <h3 class="text-2xl lg:text-3xl font-bold text-gray-900">
                    Vous n'avez pas encore de carte de fidélité ?
                  </h3>
                  <p class="text-base lg:text-lg text-gray-600 leading-relaxed max-w-xl">
                    Contactez-nous pour digitaliser votre carte de fidélité ou intégrer celle de votre groupement
                    directement dans Apodis.
                  </p>
                </div>

                <div class="pt-2">
                  <BaseButton
                    primary
                    @click="showTutorialVideo"
                  >
                    <span class="flex items-center gap-2">

                      <TrendingUp class="w-5 h-5 text-white" />
                      Voir les avantages de la carte Apodis
                    </span>
                  </BaseButton>
                  <Dialog v-model:open="tutorialModal">
                    <DialogContent>
                      <video v-if="data?.data.length" :src="data.data[0].url" loop autoplay controls />
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div class="flex-shrink-0 w-full lg:w-auto lg:max-w-md">
                <div class="relative">
                  <div
                    class="absolute inset-0 bg-gradient-to-tr from-blue-400 to-indigo-400 rounded-xl transform rotate-3 opacity-20"
                  />
                  <img
                    src="@/assets/images/loyalty-card-advantages.png"
                    class="relative w-full h-auto rounded-xl shadow-lg"
                    alt="Avantages carte de fidélité"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {LoyaltyCardMembership} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {useRouter} from "vue-router";
import {onMounted, ref} from "vue";
import {loyaltyCardApi, loyaltyCardMembershipApi} from "@/container";
import {LoyaltyCard} from "@/loyalty-card/interfaces/LoyaltyCard";
import {usePatientStore} from "@/stores/patient.store";
import {storeToRefs} from "pinia";
import BaseButton from "@/core/components/base/BaseButton.vue";
import LoyaltyCardMembershipPointBalance
    from "@/loyalty-card-membership/components/loyalty-card-membership-balances/LoyaltyCardMembershipPointBalance.vue";
import LoyaltyCardService from "@/loyalty-card/services/LoyaltyCardService";
import LoyaltyCardMembershipProductBalance
    from "@/loyalty-card-membership/components/loyalty-card-membership-balances/LoyaltyCardMembershipProductBalance.vue";
import {useToastStore} from "@/stores/toast.store";
import {Skeleton} from "@/core/components/ui/skeleton";
import {LucideCreditCard, TrendingUp} from "@lucide/vue";
import useVideos from "@/video/composables/useVideos";
import {Dialog, DialogContent} from "@/core/components/ui/dialog";

interface LoyaltyCardWithMembership extends LoyaltyCard {
    membership?: LoyaltyCardMembership;
}

const props = defineProps<{ customerId: number }>();
const loyaltyCards = ref<LoyaltyCardWithMembership[]>();
const loading = ref<boolean>(true);
const tutorialModal = ref<boolean>(false);
const cardLoading = ref<{ loyaltyCardId: number, loading: boolean }>();
const {data} = useVideos({
    limit: 1,
    tags: ['loyalty-card-tutorial']
});
const {memberships} = storeToRefs(usePatientStore());
const {loadMemberships} = usePatientStore();
const router = useRouter();
const {showSuccess} = useToastStore();

/**
 * On mount, fetch all available loyaltu card, and associate matching membership if there is one.
 */
onMounted(() => {
    loyaltyCardApi().get()
        .then(cards => {
            mapLoyaltyCardWithMembership(cards);
        })
        .finally(() => loading.value = false);
});

/**
 * Map loyalty card to add membership information to them when it exists.
 */
const mapLoyaltyCardWithMembership = (cards: LoyaltyCardWithMembership[]) => {
    loyaltyCards.value = cards.map((card: LoyaltyCardWithMembership) => {
        if (memberships.value?.length) {
            memberships.value.forEach((membership: LoyaltyCardMembership) => {
                if (membership.loyalty_card.id === card.id) {
                    card.membership = membership;
                }
            });
        }

        return card;
    });
};

/**
 * Redirect to membership page.
 * @param membership
 */
const showMembership = (membership: LoyaltyCardMembership) => {
    router.push({
        name: 'customer.loyalty.membership',

        params: {
            customerId: props.customerId,
            membershipId: membership.id
        }
    });
};

/**
 * Display a tutorial video if there is one available.
 */
const showTutorialVideo = () => {
    if (data.value && data.value.data.length > 0) {
        tutorialModal.value = true;
    }
};

const activate = (card: LoyaltyCardWithMembership) => {
    cardLoading.value = {loyaltyCardId: card.id, loading: true};
    return loyaltyCardMembershipApi()
        .create(card.id, props.customerId)
        .then(() => loadMemberships(props.customerId))
        .then(() => {
            if (loyaltyCards.value) {
                mapLoyaltyCardWithMembership(loyaltyCards.value);
            }
            showSuccess({content: "La carte de fidélité a été activé avec succès"});
        })
        .finally(() => cardLoading.value = {loyaltyCardId: card.id, loading: false});
};
</script>
